import React, { useState, useMemo, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import CountrySelect from '../../components/Common-components/region/CountrySelect';
import PhoneCodeSelect from '../../components/Common-components/region/PhoneCodeSelect';
import BaseSelect from '../../components/Common-components/region/BaseSelect';
import countries from 'i18n-iso-countries';
import ko from 'i18n-iso-countries/langs/ko.json';
import en from 'i18n-iso-countries/langs/en.json';
import * as countryCodes from 'country-codes-list';
import { useNavigate, useParams } from 'react-router-dom';
import ReactDOM from 'react-dom';
import '../../css/Booking/guestWriteInfo.css';
import { ReactComponent as UserIcon } from '../../assets/icons/svg/userIcon.svg';
import Accordion from 'react-bootstrap/Accordion';
import { ReactComponent as ArrowCircleIcon } from '../../assets/icons/svg/arrowCircle.svg';
import { apiGet, apiPost } from '../../utils/api';
// ★★★ 1. 로딩 컴포넌트 import ★★★
import Loading from '../../components/Common-components/loading';
import {
    buildBookingBasketCacheKey,
    buildBookingBasketUrl,
    getBookingBasketCachePrefix,
    getBookingGroupsCacheKey,
    isBookingBasketCacheKeyForSchedule,
    resolveBookingResultNo,
} from '../../utils/traveltekBookingApi';
import {
    findSelectedTraveltekGratuity,
    buildTraveltekPriceTranslations,
    getTraveltekPriceLabel,
    findTraveltekGratuityExtra,
    isTraveltekGratuitySelected,
    isTraveltekGratuityPriceItem,
    normalizeTraveltekPassengerPriceItem,
} from '../../utils/traveltekPriceBreakdown';
import { PromotionDiscountRows, parsePromotionAmount } from '../../utils/promotionSummary';
import { resolveTraveltekPassengers } from '../../utils/traveltekPassengerMapping';
import useActiveEtcCodes from '../../hooks/useActiveEtcCodes';
import usePromotionPreview, { promotionPreviewChanged } from '../../hooks/usePromotionPreview';
import {
    BOOKING_SUBMISSION_POLICY,
    classifyBookingSubmissionResponse,
    isAutomaticBookingRecovery,
    createBookingSubmissionBlock,
    parseBookingSubmissionBlock,
} from '../../utils/traveltekBookingSubmissionPolicy';

countries.registerLocale(ko);
countries.registerLocale(en);

const DEFAULT_PASSENGER_LABELS = {
    adults: '성인',
    children: '소아',
};

const PASSENGER_TYPE_ORDER = ['adults', 'children'];
const PARTIAL_PAYMENT_LINE_IDS = [
    17,
    22,
    73,
];
const BASKET_FETCH_COOLDOWN_MS = 1500;
const DEFAULT_BASKET_HOLD_MINUTES = 60;
const BOOKING_MANUAL_REVIEW_MESSAGES = [
    '예약 요청 결과를 확인하지 못했습니다.',
    '중복 예약 방지를 위해 동일한 예약을 다시 진행하지 말아 주세요.',
];
const basketFetchLocks = new Map();
function isPartialPaymentLine(lineId) {
    return PARTIAL_PAYMENT_LINE_IDS.includes(Number(lineId));
}

function formatUsdCurrency(value) {
    return Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatBasketHoldLocalString(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return '';
    }
    const userTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formatter = new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: userTZ,
    });
    return formatter.format(date);
}

function parseBasketHoldDate(raw) {
    if (!raw || typeof raw !== 'string') return null;
    const utcSafe = `${raw.trim().replace(' ', 'T')}Z`;
    const parsed = new Date(utcSafe);
    if (Number.isNaN(parsed.getTime())) return null;
    return {
        raw,
        iso: parsed.toISOString(),
        localString: formatBasketHoldLocalString(parsed),
    };
}

function buildFallbackBasketHold(minutes = DEFAULT_BASKET_HOLD_MINUTES) {
    const holdAt = new Date();
    const releaseAt = new Date(holdAt.getTime() + (minutes * 60 * 1000));
    return {
        holdAt: holdAt.toISOString(),
        releaseAt: releaseAt.toISOString(),
        holdAtFormatted: formatBasketHoldLocalString(holdAt),
        releaseAtFormatted: formatBasketHoldLocalString(releaseAt),
        holdAtOriginal: '',
        releaseAtOriginal: '',
        capturedAt: Date.now(),
        isFallback: true,
    };
}

function formatDueDateStatus(value) {
    if (!value) return '-';
    return `${String(value).slice(0, 10)}까지`;
}

function normalizePassengerCounts(counts) {
    if (!counts || typeof counts !== 'object') {
        return { adults: 0, children: 0 };
    }
    const adults = Number(counts.adults) || 0;
    const children = Number(counts.children) || 0;
    return { adults, children };
}

function firstNonEmpty(...values) {
    for (const value of values) {
        if (value === null || value === undefined) continue;
        const normalized = String(value).trim();
        if (normalized) return normalized;
    }
    return '';
}

function normalizeMembershipGender(value) {
    const normalized = String(value || '').trim().toLowerCase();
    if (['m', 'male', 'mr', 'sir'].includes(normalized)) return 'male';
    if (['f', 'female', 'ms', 'mrs', 'miss', 'madam'].includes(normalized)) return 'female';
    return '';
}

function normalizeMembershipPassengerInfo(value) {
    if (!value || typeof value !== 'object') return null;
    const firstName = firstNonEmpty(value.firstName, value.firstname, value.first_name, value.forename, value.givenname);
    const lastName = firstNonEmpty(value.lastName, value.lastname, value.last_name, value.surname, value.familyname);
    const birthDate = firstNonEmpty(value.birthDate, value.dob, value.dateofbirth, value.date_of_birth);
    const gender = normalizeMembershipGender(firstNonEmpty(value.gender, value.sex, value.title));
    if (!firstName && !lastName && !birthDate && !gender) return null;
    return { firstName, lastName, birthDate, gender };
}

function cleanupBasketFetchLocks(now) {
    basketFetchLocks.forEach((value, key) => {
        if (!value?.inFlight && (now - (value?.lastAt || 0)) > 10000) {
            basketFetchLocks.delete(key);
        }
    });
}

function acquireBasketFetchLock(requestKey) {
    const now = Date.now();
    cleanupBasketFetchLocks(now);
    const existing = basketFetchLocks.get(requestKey);
    if (existing?.inFlight) return false;
    if (existing?.lastAt && (now - existing.lastAt) < BASKET_FETCH_COOLDOWN_MS) {
        return false;
    }
    basketFetchLocks.set(requestKey, { inFlight: true, lastAt: now });
    return true;
}

function releaseBasketFetchLock(requestKey) {
    if (!requestKey) return;
    basketFetchLocks.set(requestKey, { inFlight: false, lastAt: Date.now() });
}

function normalizeTranslationKeyword(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/[^a-z0-9가-힣]/g, '');
}

function extractTranslationKeys(value) {
    const raw = String(value || '').trim();
    if (!raw) {
        return [];
    }

    const normalizedWhole = normalizeTranslationKeyword(raw);
    const segmentKeys = raw
        .split(/[\n,/|]+/g)
        .map(normalizeTranslationKeyword)
        .filter(Boolean);

    return Array.from(new Set([
        normalizedWhole,
        ...segmentKeys,
    ].filter(Boolean)));
}

function parseCodeTranslationRows(payload) {
    const rows = Array.isArray(payload?.data) ? payload.data : [];

    return rows
        .map((row) => {
            const en = String(Array.isArray(row) ? row[3] ?? '' : row?.en ?? '').trim();
            const kr = String(Array.isArray(row) ? row[4] ?? '' : row?.kr ?? '').trim();
            const value = String(Array.isArray(row) ? row[5] ?? '' : row?.value ?? '').trim();
            const rawKeys = [en];

            if (value && value !== '0') {
                rawKeys.push(value);
            }

            const keys = rawKeys.flatMap(extractTranslationKeys);

            if (!kr || keys.length === 0) {
                return null;
            }

            return {
                kr,
                keys: Array.from(new Set(keys)),
            };
        })
        .filter(Boolean);
}

function resolveDinnerTranslation(option, translationRows) {
    const description = String(option?.description ?? '').trim();
    const code = String(option?.code ?? '').trim();
    const normalizedDescription = normalizeTranslationKeyword(description);
    const normalizedCode = normalizeTranslationKeyword(code);
    const haystacks = [normalizedDescription, normalizedCode].filter(Boolean);

    if (haystacks.length === 0 || !Array.isArray(translationRows) || translationRows.length === 0) {
        return '';
    }

    const exactMatch = translationRows.find((row) => (
        row.keys.some((key) => haystacks.includes(key))
    ));
    if (exactMatch?.kr) {
        return exactMatch.kr;
    }
    return '';
}

function translateDiningOptions(options, translationRows) {
    if (!Array.isArray(options)) {
        return [];
    }

    return options.map((option) => {
        const translatedDescription = resolveDinnerTranslation(option, translationRows);
        return {
            ...option,
            description_ko: translatedDescription || '',
            displayDescription: translatedDescription || option?.description || option?.code || '',
        };
    });
}


// --- Helper Functions ---

const validateField = (name, value) => {
    // ... (이 부분은 원본과 동일)
    switch (name) {
        case 'lastName':
            return value ? '' : '성을 입력해주세요.';
        case 'firstName':
            return value ? '' : '이름을 입력해주세요.';
        case 'gender':
            return value ? '' : '성별을 선택해주세요.';
        case 'birthDate':
            if (!value) return '생년월일을 입력해주세요.';
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'YYYY-MM-DD 형식으로 입력해주세요.';
            return '';
        case 'passportCountry':
            return value ? '' : '국적을 선택해주세요.';
        case 'phoneNumber':
            if (!value) return '휴대폰 번호를 입력해주세요.';
            const numericValue = value.replace(/\D/g, '');
            if (!/^\d+$/.test(numericValue)) return '숫자만 입력해주세요.';
            return '';
        case 'dinnerTime':
            return value ? '' : '저녁 정찬 시간을 선택해주세요.';
        default:
            return '';
    }
};

// 국제식 번호 Ex) +82-10-1111-2222
const formatInternationalPhone = (dialCode, rawNumber) => {
    const normalizedDial = String(dialCode || '').trim();
    const digits = String(rawNumber || '').replace(/\D/g, '');
    if (!normalizedDial || !digits) return '';

    let local = digits;
    if (local.startsWith('0')) {
        local = local.slice(1);
    }

    let formattedLocal = local;
    if (local.length === 10) {
        formattedLocal = `${local.slice(0, 2)}-${local.slice(2, 6)}-${local.slice(6)}`;
    } else if (local.length === 9) {
        formattedLocal = `${local.slice(0, 2)}-${local.slice(2, 5)}-${local.slice(5)}`;
    } else if (local.length === 8) {
        formattedLocal = `${local.slice(0, 4)}-${local.slice(4)}`;
    } else if (local.length > 0) {
        formattedLocal = local.replace(/(\d{3})(?=\d)/g, '$1-');
    }

    return `${normalizedDial.replace(/-+$/, '')}-${formattedLocal}`;
};

// --- Child Components ---
// 사용자 동시 바스켓 정보 획득시 perperson기준으로 예약가능/불가능 구분 후 input disabled + 클릭박지 + 안내메세지 추가
function UserInfo({
    type,
    idx,
    idSuffix,
    userData,
    onDataChange,
    onBlur,
    errors,
    diningOptions,
    formDisabled = false,
    isRepresentative = false,
    passengerLabels = DEFAULT_PASSENGER_LABELS,
    ageTypeWarning = '',
}) {
    const label = passengerLabels[type] || type;
    const suffix = String(idSuffix ?? idx);
    const eventKey = suffix;

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        // 이름 필드 영어만 허용
        if (name === 'lastName' || name === 'firstName') {
            processedValue = value.replace(/[^a-zA-Z\s]/g, '');
        }

        // Birth Date 하이픈 자동 추가 (YYYY-MM-DD)
        if (name === 'birthDate') {
            // 숫자만 추출
            let numericValue = value.replace(/\D/g, '');
            if (numericValue.length > 4 && numericValue.length <= 6) {
                processedValue = `${numericValue.slice(0,4)}-${numericValue.slice(4)}`;
            } else if (numericValue.length > 6 && numericValue.length <= 8) {
                processedValue = `${numericValue.slice(0,4)}-${numericValue.slice(4,6)}-${numericValue.slice(6)}`;
            } else {
                processedValue = numericValue;
            }
        }

        // 전화번호 하이픈 자동 추가
        if (name === 'phoneNumber') {
            // 숫자만 추출
            processedValue = value.replace(/\D/g, '');
            // 자동 하이픈 추가 (010-1111-2222 형태)
            if (processedValue.length <= 3) {
                processedValue = processedValue;
            } else if (processedValue.length <= 7) {
                processedValue = `${processedValue.slice(0,3)}-${processedValue.slice(3)}`;
            } else if (processedValue.length <= 11) {
                processedValue = `${processedValue.slice(0,3)}-${processedValue.slice(3,7)}-${processedValue.slice(7,11)}`;
            } else {
                processedValue = `${processedValue.slice(0,3)}-${processedValue.slice(3,7)}-${processedValue.slice(7,11)}`;
            }
        }

        onDataChange(name, processedValue);
    };

    const handleGenderChange = (value) => {
        onDataChange('gender', value);
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if ((name === 'lastName' || name === 'firstName') && value) {
            // 공백이 있는 경우 각 단어 첫 글자만 대문자로
            processedValue = value
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        }

        onBlur(name, processedValue);
    };

    // 전화국가번호 옵션 생성 (+82 등)
    const phoneOptions = useMemo(() => {
        const cc = (countryCodes && countryCodes.customList)
            ? countryCodes
            : (countryCodes && countryCodes.default && countryCodes.default.customList)
                ? countryCodes.default
                : null;
        if (!cc) return [];
        const rawMap = cc.customList('countryCode', '{countryNameEn}|{countryCallingCode}');
        const list = Object.entries(rawMap).map(([alpha2, str]) => {
            const [nameEnFromList, calling] = String(str).split('|');
            const nameKo = countries.getName(alpha2, 'ko');
            const nameEnIso = countries.getName(alpha2, 'en');
            const name = nameKo || nameEnIso || nameEnFromList || alpha2;
            const dial = calling ? `+${calling}` : '';
            return dial ? { alpha2, name, dial } : null;
        }).filter(Boolean);
        const priority = ['KR', 'US', 'JP'];
        const prio = list.filter(o => priority.includes(o.alpha2));
        const rest = list.filter(o => !priority.includes(o.alpha2)).sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        return [...prio, ...rest];
    }, []);

    const dinnerSelectOptions = useMemo(() => (
        Array.isArray(diningOptions)
            ? diningOptions
                .filter(option => option?.status && option.status.toLowerCase() === 'available')
                .map(option => ({
                    value: option.code,
                    label: option.displayDescription || option.description || option.code,
                    key: option.code,
                }))
            : []
    ), [diningOptions]);

    return (
        <div className="user-info">
            <Accordion defaultActiveKey={eventKey}>
                <Accordion.Item eventKey={eventKey}>
                    <Accordion.Header>
                        <div><UserIcon /> {label} {idx}</div>
                    </Accordion.Header>
                    <Accordion.Body>
                        <div className='user-info-form' id={`user_info_form_${suffix}`}>
                            {/* Last Name */}
                            <div className='form-item'>
                                <label htmlFor={`last-name-${suffix}`} className='require'>성(Last Name)</label>
                                <input
                                    id={`last-name-${suffix}`}
                                    name='lastName'
                                    type='text'
                                    placeholder='성을 입력해주세요 (예: Hong)'
                                    className={`form-input ${errors?.lastName ? 'is-invalid' : ''}`}
                                    value={userData.lastName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={formDisabled}
                                />
                                {errors?.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                            </div>
                            {/* First Name */}
                            <div className='form-item'>
                                <label htmlFor={`first-name-${suffix}`} className='require'>이름(First Name)</label>
                                <input
                                    id={`first-name-${suffix}`}
                                    name='firstName'
                                    type='text'
                                    placeholder='이름을 입력해주세요 (예: Gildong)'
                                    className={`form-input ${errors?.firstName ? 'is-invalid' : ''}`}
                                    value={userData.firstName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={formDisabled}
                                />
                                {errors?.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                            </div>
                            {/* Gender */}
                            <div className='form-item'>
                                <span className='require'>성별</span>
                                <div className="gender-radio-group">
                                    <label htmlFor={`gender-male-${suffix}`} className="custom-radio-label">
                                        <input type="radio" id={`gender-male-${suffix}`} name={`gender-${suffix}`} value="male" checked={userData.gender === 'male'} onChange={() => handleGenderChange('male')} disabled={formDisabled} />
                                        남성
                                    </label>
                                    <label htmlFor={`gender-female-${suffix}`} className="custom-radio-label">
                                        <input type="radio" id={`gender-female-${suffix}`} name={`gender-${suffix}`} value="female" checked={userData.gender === 'female'} onChange={() => handleGenderChange('female')} disabled={formDisabled} />
                                        여성
                                    </label>
                                </div>
                                {errors?.gender && <div className="invalid-feedback" style={{ display: 'block' }}>{errors.gender}</div>}
                            </div>
                            {/* Birth Date */}
                            <div className='form-item'>
                                <label htmlFor={`birth-date-${suffix}`} className='require'>생년월일</label>
                                {ageTypeWarning && (
                                    <span className='age-type-warning'>
                                        {ageTypeWarning}
                                    </span>
                                )}
                                <input
                                    id={`birth-date-${suffix}`}
                                    name='birthDate'
                                    type='text'
                                    placeholder='생년월일을 입력해 주세요. (예:2024-12-30)'
                                    className={`form-input ${errors?.birthDate ? 'is-invalid' : ''}`}
                                    value={userData.birthDate}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={formDisabled}
                                />
                                {errors?.birthDate && <div className="invalid-feedback">{errors.birthDate}</div>}
                            </div>
                            {/* Passport Country */}
                            <div className='form-item'>
                                <label className='require'>국적</label>
                                <CountrySelect
                                    value={userData.passportCountry}
                                    onChange={(val) => {
                                        // 값은 ISO2 코드(KR/JP/CN...)로 유지
                                        handleChange({ target: { name: 'passportCountry', value: val } });
                                        // blur 시점 검증도 동일하게 트리거
                                        handleBlur({ target: { name: 'passportCountry', value: val } });
                                    }}
                                    className={`${errors?.passportCountry ? 'is-invalid' : ''}`}
                                    defaultOptionLabel="여권 발행국가를 선택해 주세요."
                                    {...(formDisabled ? { disabled: true } : {})}
                                />
                                {errors?.passportCountry && <div className="invalid-feedback">{errors.passportCountry}</div>}
                            </div>
                            {/* Phone Number */}
                            <div className='form-item'>
                                <label htmlFor={`phone-number-${suffix}`} className='require'>휴대폰 번호</label>
                                <div className='form-phone-group'>
                                    <PhoneCodeSelect
                                        value={userData.phoneCountryCode}
                                        onChange={(val) => handleChange({ target: { name: 'phoneCountryCode', value: val } })}
                                        options={phoneOptions}
                                        className=''
                                        {...(formDisabled ? { disabled: true } : {})}
                                    />
                                    <input
                                        id={`phone-number-${suffix}`}
                                        name='phoneNumber'
                                        type='tel'
                                        placeholder='번호를 입력해 주세요.'
                                        className={`form-input ${errors?.phoneNumber ? 'is-invalid' : ''}`}
                                        value={userData.phoneNumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={formDisabled}
                                    />
                                </div>
                                {errors?.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber}</div>}
                            </div>
                            {/* 저녁 정찬 시간 */}
                            {isRepresentative && (
                                <div className='form-item'>
                                    <label className='require'>저녁 정찬 시간</label>
                                    <BaseSelect
                                        options={dinnerSelectOptions}
                                        value={userData.dinnerTime}
                                        onChange={(val) => {
                                            handleChange({ target: { name: 'dinnerTime', value: val } });
                                            handleBlur({ target: { name: 'dinnerTime', value: val } });
                                        }}
                                        placeholder='저녁 정찬 시간을 선택해 주세요.'
                                        className={errors?.dinnerTime ? 'is-invalid' : ''}
                                        disabled={formDisabled}
                                        ariaLabel='저녁 정찬 시간 선택'
                                    />
                                    {errors?.dinnerTime && <div className="invalid-feedback">{errors.dinnerTime}</div>}
                                </div>
                            )}
                            {/*
                            <div className='form-item'>
                                <label htmlFor={`bed-type-${suffix}`}>침대 타입(선택사항)</label>
                                <select
                                    id={`bed-type-${suffix}`}
                                    name='bedType'
                                    className={`form-select`}
                                    value={userData.bedType}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={formDisabled}
                                >
                                    <option value="">침대 타입을 선택해 주세요.</option>
                                    <option value="single">싱글</option>
                                    <option value="super-single">싱글</option>
                                    <option value="queen">퀸</option>
                                    <option value="king">킹</option>
                                    <option value="large-king">라지 킹</option>
                                </select>
                                {errors?.bedType && <div className="invalid-feedback">{errors.bedType}</div>}
                            </div>

                            <div className='form-item'>
                                <label htmlFor={`membership-number-${suffix}`}>탑승 선사 멤버쉽 번호(선택사항)</label>
                                <input
                                    id={`membership-number-${suffix}`}
                                    name='membershipNumber'
                                    type='text'
                                    placeholder='멤버쉽 번호를 입력해 주세요'
                                    className='form-input'
                                    value={userData.membershipNumber}
                                    onChange={handleChange}
                                    disabled={formDisabled}
                                />
                            </div>
                            */}
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    );
}

const RequestInfoBtn = ({ reservationType, onClick, disabled }) => (
    <button
        type='button'
        onClick={onClick}
        disabled={disabled}
        className={`${reservationType ? 'payment-btn' : 'request-reservation-btn'} btn`}
    >
        {reservationType ? '결제하기' : '예약 요청하기'}
    </button>
);

// --- Main Component ---

// isRestoring=true면 스냅샷에서 복구 중이라는 의미로, 기존 캐시/홀드 정보를 재사용해 API 재호출을 피한다.
function GuestWriteInfo({
    bookingPayload,
    onNextStep,
    onResetBookingFlow,
    onBookingSubmissionBlockChange,
    userEmail = '',
    ageConfig,
    isRestoring = false,
}) {
    const navigate = useNavigate();
    const { idx } = useParams();
    // --- Original Data from Payload (for Forms) ---
    const rawCounts = bookingPayload?.counts || {};
    const counts = useMemo(() => normalizePassengerCounts(rawCounts), [rawCounts]);
    const passengerLabels = useMemo(() => ({
        adults: ageConfig?.adult?.summaryLabel ?? DEFAULT_PASSENGER_LABELS.adults,
        children: ageConfig?.child?.summaryLabel ?? DEFAULT_PASSENGER_LABELS.children,
    }), [ageConfig]);

    // 성인나이로 구분 -> 각 선사 line_id로 구분해서 진행
    const adultMinAge = useMemo(() => {
        const parsed = Number(ageConfig?.adult?.minAge);
        return Number.isFinite(parsed) ? parsed : 18; // MSC기준 기본 18세
    }, [ageConfig?.adult?.minAge]);
    const childMinAge = useMemo(() => {
        const parsed = Number(ageConfig?.child?.minAge);
        return Number.isFinite(parsed) ? parsed : null;
    }, [ageConfig?.child?.minAge]);
    const childMaxAge = useMemo(() => {
        const parsed = Number(ageConfig?.child?.maxAge);
        return Number.isFinite(parsed) ? parsed : null;
    }, [ageConfig?.child?.maxAge]);
    const departureDate = useMemo(() => {
        const raw =
            bookingPayload?.cruise?.departureDate ||
            bookingPayload?.detailData?.schedule?.saildate ||
            bookingPayload?.detailData?.schedule?.departure_date;
        if (!raw) return null;
        const resolved = new Date(raw);
        return Number.isNaN(resolved.getTime()) ? null : resolved;
    }, [bookingPayload?.cruise?.departureDate, bookingPayload?.detailData?.schedule?.saildate, bookingPayload?.detailData?.schedule?.departure_date]);

    const payloadCabinName = bookingPayload?.carbinGradeInfo?.grade_name_ko
        || bookingPayload?.carbinGradeInfo?.grade_name;
    // 1단계에서 입력한 소아 나이
    const childAges = useMemo(() => {
        const rawChildren = bookingPayload?.ages?.children;
        if (!Array.isArray(rawChildren)) return [];
        return rawChildren.map(value => {
            if (value === null || value === undefined) return null;
            const normalized = typeof value === 'string' ? value.trim() : value;
            if (normalized === '') return null;
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : null;
        });
    }, [bookingPayload?.ages]);

    // --- API Data State ---
    const [cruiseBasketInfo, setCruiseBasketInfo] = useState(null);
    const [isPriceBlocked, setIsPriceBlocked] = useState(false); // perperson 미존재 시 가격 표시 차단
    const [lineId, setLineId] = useState(() => (
        bookingPayload?.detailData?.schedule?.line_id ||
        bookingPayload?.detailData?.schedule?.lineid ||
        null
    ));
    const [isExtraUpdating, setIsExtraUpdating] = useState(false);
    // ★★★ 2. 로딩 상태를 관리할 state 추가 ★★★
    const [isLoading, setIsLoading] = useState(false);
    // 이 화면에서 응답을 기다리는 예약 요청만 구분한다. 복원된 차단 정보와
    // 일반 조회/부가서비스 로딩은 결과 확인 경고를 숨기는 근거가 아니다.
    const [isBookingRequestPending, setIsBookingRequestPending] = useState(false);
    const bookingSubmissionBlockKey = idx ? `booking:submissionBlocked:${idx}` : null;
    const [bookingSubmissionBlock, setBookingSubmissionBlock] = useState(() => {
        if (!idx || typeof sessionStorage === 'undefined') return null;
        try {
            const raw = sessionStorage.getItem(`booking:submissionBlocked:${idx}`);
            return parseBookingSubmissionBlock(raw);
        } catch (_) {
            return parseBookingSubmissionBlock('{corrupt');
        }
    });
    const bookingSubmissionBlocked = Boolean(bookingSubmissionBlock);
    const activeEtcCodes = useActiveEtcCodes();
    const dinnerTranslationRows = useMemo(() => parseCodeTranslationRows({ data: activeEtcCodes }), [activeEtcCodes]);
    const priceTranslations = useMemo(() => buildTraveltekPriceTranslations(activeEtcCodes), [activeEtcCodes]);
    const basketMetaRef = useRef(null);
    // state 반영 전 연속 클릭으로 book.pl이 두 번 호출되는 것을 동기적으로 차단한다.
    const submitInFlightRef = useRef(false);
    const extraUpdateInFlightRef = useRef(false);
    const activeSubmissionAttemptRef = useRef(null);
    const componentMountedRef = useRef(false);
    const latestBookingContextRef = useRef({ scheduleId: '', sessionKey: '' });

    useLayoutEffect(() => {
        componentMountedRef.current = true;
        return () => {
            componentMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        basketMetaRef.current = cruiseBasketInfo?.meta || null;
    }, [cruiseBasketInfo?.meta]);

    useEffect(() => {
        if (!bookingSubmissionBlockKey || typeof sessionStorage === 'undefined') {
            setBookingSubmissionBlock(null);
            return;
        }
        try {
            const raw = sessionStorage.getItem(bookingSubmissionBlockKey);
            setBookingSubmissionBlock(parseBookingSubmissionBlock(raw));
        } catch (_) {
            setBookingSubmissionBlock(parseBookingSubmissionBlock('{corrupt'));
        }
    }, [bookingSubmissionBlockKey]);

    const cruiseCodeToId = bookingPayload?.cruise?.codeToCruiseId || bookingPayload?.cruise?.id || bookingPayload?.cruise?.code;
    const resolveLineId = useCallback((data) => {
        const item = data?.results?.[0]?.basketitems?.[0];
        return (
            item?.cruisedetail?.line_id ||
            item?.cruisedetail?.lineid ||
            bookingPayload?.detailData?.schedule?.line_id ||
            bookingPayload?.detailData?.schedule?.lineid ||
            null
        );
    }, [bookingPayload?.detailData?.schedule?.line_id, bookingPayload?.detailData?.schedule?.lineid]);

    const getBasketCacheInfo = useCallback(() => {
        const gradeInfo = bookingPayload?.carbinGradeInfo;
        const selectedPricing = gradeInfo?.gridPrice;
        const resultNO = resolveBookingResultNo(gradeInfo);
        const gradeNo = selectedPricing?.gradeno ?? selectedPricing?.rate?.gradeno ?? gradeInfo?.gradeno;
        const rateCode = selectedPricing?.rate?.code ?? selectedPricing?.ratecode;
        const cabinResult = bookingPayload?.userSelectCabinInfo?.resultno;
        if ((!resultNO && !cruiseCodeToId) || !gradeNo || !rateCode || !cabinResult) {
            return null;
        }
        const basketPrefix = getBookingBasketCachePrefix(idx != null ? idx : null);
        const cacheKey = buildBookingBasketCacheKey({
            scheduleId: idx != null ? idx : null,
            cruiseId: cruiseCodeToId,
            resultNO,
            gradeNo,
            rateCode,
            cabinResult,
        });
        return { cacheKey, basketPrefix };
    }, [bookingPayload?.carbinGradeInfo, bookingPayload?.userSelectCabinInfo?.resultno, idx, cruiseCodeToId]);

    const normalizeBasketPayload = useCallback((data) => {
        if (!data) return null;
        let normalized = data;
        if (!normalized.results && normalized.data && normalized.data.results) {
            normalized = normalized.data;
        }
        if (!normalized.results) return null;
        if (!normalized.meta && basketMetaRef.current) {
            normalized = { ...normalized, meta: basketMetaRef.current };
        }
        return normalized;
    }, []);

    const persistBasketCache = useCallback((basketData) => {
        const cacheInfo = getBasketCacheInfo();
        if (!cacheInfo || !cacheInfo.cacheKey) return;
        try {
            const removeKeys = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith(cacheInfo.basketPrefix) && key !== cacheInfo.cacheKey) {
                    removeKeys.push(key);
                }
            }
            removeKeys.forEach(key => sessionStorage.removeItem(key));
            sessionStorage.setItem(cacheInfo.cacheKey, JSON.stringify(basketData));
        } catch (_) {}
    }, [getBasketCacheInfo]);

    const applyBasketData = useCallback((basketData, options = {}) => {
        const normalized = normalizeBasketPayload(basketData);
        if (!normalized) return;
        setCruiseBasketInfo(normalized);
        const item0 = normalized?.results?.[0]?.basketitems?.[0];
        const hasPerperson = Array.isArray(item0?.cruisedetail?.perperson) && item0.cruisedetail.perperson.length > 0;
        setIsPriceBlocked(!hasPerperson);
        const resolvedLineId = resolveLineId(normalized);
        if (resolvedLineId) {
            setLineId(resolvedLineId);
        }
        if (options.updateCache !== false) {
            persistBasketCache(normalized);
        }
    }, [normalizeBasketPayload, persistBasketCache, resolveLineId]);
    // 스냅샷 초기화
    const resetBookingState = useCallback(() => {
        if (!componentMountedRef.current
            || latestBookingContextRef.current.scheduleId !== String(idx == null ? '' : idx)) {
            return false;
        }
        try {
            const scheduleKey = idx == null ? '' : String(idx);
            const storedCruiseId = scheduleKey
                ? (sessionStorage.getItem(`booking:scheduleToCruiseId:${scheduleKey}`) || '')
                : '';
            const mappingMatches = Boolean(
                cruiseCodeToId && storedCruiseId === String(cruiseCodeToId)
            );
            const scopedSessionKey = scheduleKey
                ? (sessionStorage.getItem(`booking:sessionKey:${scheduleKey}`) || '')
                : '';
            const cruiseSessionKey = cruiseCodeToId
                ? (sessionStorage.getItem(`booking:sessionKey:${cruiseCodeToId}`) || '')
                : '';
            const currentSessionKey = scopedSessionKey || cruiseSessionKey;
            const keysToRemove = scheduleKey ? [
                `booking:snapshot:${scheduleKey}`,
                `booking:sessionKey:${scheduleKey}`,
                `booking:sessionKeyIssuedAt:${scheduleKey}`,
                `booking:sessionKeyIssuedAt:schedule:${scheduleKey}`,
                getBookingGroupsCacheKey(scheduleKey),
                `booking:groups:${scheduleKey}`,
                `booking:skipGroupsCache:${scheduleKey}`,
                `booking:basketHold:${scheduleKey}`,
                `booking:basketSkipFetch:${scheduleKey}`,
                `booking:scheduleToCruiseId:${scheduleKey}`,
            ].filter(Boolean) : [];

            if (cruiseCodeToId && (
                (currentSessionKey && cruiseSessionKey === currentSessionKey)
                || (mappingMatches && !cruiseSessionKey)
            )) {
                keysToRemove.push(
                    `booking:sessionKey:${cruiseCodeToId}`,
                    `booking:sessionKeyIssuedAt:${cruiseCodeToId}`,
                );
            }
            if (currentSessionKey && sessionStorage.getItem('bookingSessionKey') === currentSessionKey) {
                keysToRemove.push('bookingSessionKey', 'bookingSessionKeyIssuedAt');
            }

            const removableKeys = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (isBookingBasketCacheKeyForSchedule(key, scheduleKey)) removableKeys.push(key);
            }
            [...new Set([...keysToRemove, ...removableKeys])]
                .forEach(key => sessionStorage.removeItem(key));
        } catch (_) {}

        const targetPath = idx ? `/booking/${idx}` : '/booking';
        navigate(targetPath, { replace: true, state: { step: 0 } });
        return true;
    }, [navigate, idx, cruiseCodeToId]);

    const resolveScopedSessionKey = useCallback(() => {
        if (typeof sessionStorage === 'undefined') return '';
        const candidates = [];
        if (idx) candidates.push(`booking:sessionKey:${idx}`);
        if (cruiseCodeToId) candidates.push(`booking:sessionKey:${cruiseCodeToId}`);
        candidates.push('bookingSessionKey');
        for (const key of candidates) {
            try {
                const val = sessionStorage.getItem(key);
                if (val) return val;
            } catch (_) { /* noop */ }
        }
        return '';
    }, [idx, cruiseCodeToId]);
    // 이전 렌더의 비동기 응답도 항상 최신 항차/세션과 비교할 수 있도록
    // 렌더마다 ref를 갱신한다(이전 콜백 클로저가 읽어도 최신 값이다).
    latestBookingContextRef.current = {
        scheduleId: idx == null ? '' : String(idx),
        sessionKey: resolveScopedSessionKey(),
        payload: bookingPayload,
    };

    const persistBookingSubmissionBlock = useCallback((block) => {
        setBookingSubmissionBlock(block);
        if (!block || !bookingSubmissionBlockKey || typeof sessionStorage === 'undefined') return false;
        try {
            const serialized = JSON.stringify(block);
            sessionStorage.setItem(bookingSubmissionBlockKey, serialized);
            const persisted = sessionStorage.getItem(bookingSubmissionBlockKey) === serialized;
            if (persisted && typeof onBookingSubmissionBlockChange === 'function') {
                onBookingSubmissionBlockChange(true);
            }
            return persisted;
        } catch (_) {
            return false;
        }
    }, [bookingSubmissionBlockKey, onBookingSubmissionBlockChange]);

    const clearBookingSubmissionBlock = useCallback(() => {
        if (bookingSubmissionBlockKey && typeof sessionStorage !== 'undefined') {
            try {
                sessionStorage.removeItem(bookingSubmissionBlockKey);
                if (sessionStorage.getItem(bookingSubmissionBlockKey) !== null) return false;
            } catch (_) {
                return false;
            }
        }
        setBookingSubmissionBlock(null);
        if (typeof onBookingSubmissionBlockChange === 'function') {
            onBookingSubmissionBlockChange(false);
        }
        return true;
    }, [bookingSubmissionBlockKey, onBookingSubmissionBlockChange]);

    const blockBookingSubmission = useCallback((decision, response = {}, attemptedSessionKey = '') => {
        const block = createBookingSubmissionBlock(decision, {
            scheduleId: idx,
            requestId: response?.request_id ?? response?.requestId ?? null,
            // 응답 시점의 storage가 아니라 실제 book.pl 요청 payload의 세션을 고정한다.
            expectedSessionKey: attemptedSessionKey,
        });
        if (!block) return null;
        persistBookingSubmissionBlock(block);
        return block;
    }, [idx, persistBookingSubmissionBlock]);

    const requestSafeBookingReset = useCallback((block = bookingSubmissionBlock, source = 'user') => {
        if (!componentMountedRef.current
            || latestBookingContextRef.current.scheduleId !== String(idx == null ? '' : idx)
            || latestBookingContextRef.current.sessionKey !== String(block?.expectedSessionKey || '')
            || block?.policy !== BOOKING_SUBMISSION_POLICY.RESTART_SAFE
            || !block?.expectedSessionKey
            || typeof onResetBookingFlow !== 'function') {
            return false;
        }

        const result = onResetBookingFlow({
            policy: block.policy,
            expectedSessionKey: block.expectedSessionKey,
            requestId: block.requestId || null,
            source,
        });
        const resetSucceeded = result === true || result?.ok === true;
        if (resetSucceeded) return true;

        blockBookingSubmission({
            policy: BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW,
            reason: result?.reason || 'booking_reset_postcondition_failed',
        }, { request_id: block.requestId || null }, block.expectedSessionKey);
        window.setTimeout(() => {
            alert({
                title: '예약 상태 확인이 필요합니다.',
                content: <p>예약 정보 초기화를 완료하지 못했습니다.<br />중복 예약 방지를 위해 다시 예약하지 말고 예약 상태를 확인해 주세요.</p>,
            });
        }, 0);
        return false;
    }, [bookingSubmissionBlock, blockBookingSubmission, idx, onResetBookingFlow]);

    const bookingRecoveryCallbacksRef = useRef();
    bookingRecoveryCallbacksRef.current = { blockBookingSubmission, requestSafeBookingReset };

    const basketFetchGuardRef = useRef({ inFlight: false, key: '', lastAt: 0 });

    // 객실 정보를 다시 불러오되, 기존 홀드가 살아있으면 API를 재호출하지 않는다
    useEffect(() => {
        // book.pl 요청이 진행 중이거나 결과 확인/안전 초기화를 기다리는 동안
        // 동일 세션의 basket을 다시 만들거나 갱신하지 않는다.
        if (bookingSubmissionBlocked) return;
        const fetchContext = { ...latestBookingContextRef.current };
        const isCurrentBasketRequest = () => componentMountedRef.current
            && latestBookingContextRef.current.scheduleId === fetchContext.scheduleId
            && latestBookingContextRef.current.sessionKey === fetchContext.sessionKey
            && latestBookingContextRef.current.payload === fetchContext.payload
            && resolveScopedSessionKey() === fetchContext.sessionKey
            && !submitInFlightRef.current;
        const fetchCruiseBasket = async () => {
            if (!bookingPayload) return;
            // ★★★ 로딩 시작 ★★★
            setIsLoading(true);
            let acquiredGlobalLock = false;
            let requestKey = '';
            try {
                const sessionKey = resolveScopedSessionKey();
                const gradeInfo = bookingPayload.carbinGradeInfo;
                const selectedPricing = gradeInfo.gridPrice;
                const resultNO = resolveBookingResultNo(gradeInfo);
                // gridPrice 구조가 케이스별로 상이하므로 안전하게 추출
                const gradeNo = selectedPricing?.gradeno ?? selectedPricing?.rate?.gradeno ?? gradeInfo?.gradeno;
                const rateCode = selectedPricing?.rate?.code ?? selectedPricing?.ratecode;
                const cabinResult = bookingPayload.userSelectCabinInfo.resultno;

                if ((!resultNO && !cruiseCodeToId) || !gradeNo || !rateCode || !cabinResult) {
                    console.error("Required basket parameters are missing in the booking payload.");
                    return;
                }

                const cacheKey = buildBookingBasketCacheKey({
                    scheduleId: idx != null ? idx : null,
                    cruiseId: cruiseCodeToId,
                    resultNO,
                    gradeNo,
                    rateCode,
                    cabinResult,
                });
                requestKey = `${sessionKey}:${cacheKey}`;
                const guard = basketFetchGuardRef.current;
                if (guard.inFlight && guard.key === requestKey) {
                    setIsLoading(false);
                    return;
                }
                const now = Date.now();
                if (guard.key === requestKey && (now - guard.lastAt) < 1000) {
                    setIsLoading(false);
                    return;
                }
                guard.inFlight = true;
                guard.key = requestKey;
                guard.lastAt = now;

                acquiredGlobalLock = acquireBasketFetchLock(requestKey);
                if (!acquiredGlobalLock) {
                    setIsLoading(false);
                    return;
                }

                // 캐시 키 구성: idx 별로 독립된 basket 캐시를 유지해 다른 항차와 충돌하지 않도록 함
                const cached = sessionStorage.getItem(cacheKey);
                const holdStorageKey = idx != null ? `booking:basketHold:${idx}` : null;
                const skipFetchKey = idx != null ? `booking:basketSkipFetch:${idx}` : null;
                const syncBasketHold = (payload) => {
                    if (!holdStorageKey || idx == null) return;
                    sessionStorage.setItem(holdStorageKey, JSON.stringify(payload));
                    window.dispatchEvent(new CustomEvent('basketHoldUpdated', { detail: { idx } }));
                };
                const shouldSkipBecauseHoldExpired = skipFetchKey && sessionStorage.getItem(skipFetchKey) === '1';
                let preservedHold = null;
                if (holdStorageKey) {
                    const existingHoldStr = sessionStorage.getItem(holdStorageKey);
                    if (existingHoldStr) {
                        try {
                            const parsedHold = JSON.parse(existingHoldStr);
                            const releaseMs = parsedHold?.releaseAt ? new Date(parsedHold.releaseAt).getTime() : NaN;
                            if (Number.isFinite(releaseMs) && releaseMs > Date.now()) {
                                preservedHold = parsedHold; // 아직 유효한 홀드 정보가 있으면 재사용
                            }
                        } catch (_) { /* noop */ }
                    }
                }
                if (cached) {
                    try {
                        const parsed = JSON.parse(cached);
                        if (parsed?.results?.[0]?.basketitems?.[0]?.cruisedetail?.perperson?.length > 0
                            && !parsed.errors?.length && parsed.success !== false) {
                            applyBasketData(parsed, { updateCache: false });
                            if (preservedHold || shouldSkipBecauseHoldExpired) {
                                setIsLoading(false);
                                if (shouldSkipBecauseHoldExpired) {
                                    sessionStorage.removeItem(skipFetchKey);
                                }
                                return; // 홀드가 남아있거나 만료 후 즉시 복원할 때는 캐시만 사용하고 API 생략
                            }
                            syncBasketHold(buildFallbackBasketHold());
                            setIsLoading(false);
                            return; // 공급사가 홀드를 안 줘도 캐시 기준으로 60분 fallback 홀드를 부여해 재호출을 막는다
                        }
                    } catch (_) {
                        // 캐시 파싱 실패 시 무시하고 네트워크 진행
                    }
                }

                if (shouldSkipBecauseHoldExpired) {
                    sessionStorage.removeItem(skipFetchKey);
                    basketFetchGuardRef.current.inFlight = false;
                    basketFetchGuardRef.current.lastAt = Date.now();
                    setIsLoading(false);
                    return;
                }

                // 컴포넌트 마운트 될때 api 실행
                const apiUrl = buildBookingBasketUrl({
                    sessionKey,
                    cruiseId: cruiseCodeToId,
                    shipId: bookingPayload?.detailData?.schedule?.ship_id,
                    resultNO,
                    gradeNo,
                    cabinResult,
                    rateCode,
                });
                const basketData = await apiGet(apiUrl);

                if (!isCurrentBasketRequest()) return;
                // A booking may have started while this basket request was waiting.
                if (bookingSubmissionBlockKey && sessionStorage.getItem(bookingSubmissionBlockKey)) return;
                if (basketData?.retry_policy || basketData?.errors?.length) {
                    const decision = classifyBookingSubmissionResponse({ response: basketData, expectedSessionKey: sessionKey });
                    const block = bookingRecoveryCallbacksRef.current.blockBookingSubmission(decision, basketData, sessionKey);
                    setIsPriceBlocked(true);
                    if (block?.policy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE
                        && isAutomaticBookingRecovery(basketData, sessionKey)) {
                        if (block.reason === 'cabin_reselection_required') {
                            // Keep the existing 104 notice; only its confirmation action changes.
                            alert({
                                title: '예약 상태 확인이 필요합니다.',
                                content: <p>객실 정보를 확인하지 못했습니다. 다시 예약하기 전에 고객센터에 예약 상태를 확인해 주세요.</p>,
                                showConfirmButton: false,
                                footerContent: <button type="button" className="btn">확인</button>,
                                action: () => bookingRecoveryCallbacksRef.current.requestSafeBookingReset(block, 'cabin_reselection_required'),
                            });
                            return;
                        }
                        const upstreamError = basketData?.errors?.[0] ?? basketData?.traveltek_response?.errors?.[0];
                        const returnedMessage = basketData?.message || basketData?.msg
                            || (typeof upstreamError === 'string' ? upstreamError : upstreamError?.message || upstreamError?.text);
                        alert({
                            title: '객실을 다시 선택해 주세요.',
                            content: <p>{typeof returnedMessage === 'string' && returnedMessage.trim()
                                ? returnedMessage : '선택한 객실을 확보하지 못했습니다.'}<br />확인을 누르면 예약 정보를 초기화하고 첫 단계로 이동합니다.</p>,
                            showConfirmButton: false,
                            footerContent: <button type="button" className="btn">확인</button>,
                            action: () => bookingRecoveryCallbacksRef.current.requestSafeBookingReset(block, 'cabin_hold_unavailable'),
                        });
                    } else {
                        alert({ title: '예약 상태 확인이 필요합니다.', content: <p>객실 정보를 확인하지 못했습니다. 다시 예약하기 전에 고객센터에 예약 상태를 확인해 주세요.</p> });
                    }
                    return;
                }

                const firstResult = basketData?.results?.[0];
                const firstItem = firstResult?.basketitems?.[0];

                if (!firstResult || !firstItem) {
                    setIsLoading(false);
                    alert({
                        title: '에러',
                        content: <p>예약 정보(바스켓)를 불러오지 못했습니다. 다시 시도하거나 다른 객실을 선택해주세요.</p>,
                        showConfirmButton : false,
                        footerContent: <button type="button" className="btn">확인</button>,
                        action: resetBookingState,
                    });
                    return;
                }

                if (!firstItem.itemkey) {
                    setIsLoading(false);
                    alert({
                        title: '에러',
                        content: <p>예약에 필요한 항목 정보가 없어 진행할 수 없습니다. 다시 시도하거나 다른 객실을 선택해주세요.</p>,
                        showConfirmButton : false,
                        footerContent: <button type="button" className="btn">확인</button>,
                        action: resetBookingState,
                    });
                    return;
                }

                const lineIdFromBasket = firstItem?.cruisedetail?.line_id || firstItem?.cruisedetail?.lineid;
                const lineIdFromDetail = bookingPayload?.detailData?.schedule?.line_id || bookingPayload?.detailData?.schedule?.lineid;
                const resolvedLineId = lineIdFromBasket || lineIdFromDetail;
                if (!resolvedLineId) {
                    setIsLoading(false);
                    alert({
                        title: '에러',
                        content: <p>선사 정보(line_id)가 없어 예약을 진행할 수 없습니다. 다시 시도하거나 다른 객실을 선택해주세요.</p>,
                        showConfirmButton : false,
                        footerContent: <button type="button" className="btn">확인</button>,
                        action: resetBookingState,
                    });
                    return;
                }
                setLineId(resolvedLineId);

                if (basketData.results?.length > 0 && basketData.results[0].basketitems?.length > 0) {
                    const item0 = basketData.results[0].basketitems?.[0];
                    applyBasketData(basketData);

                    // 객실 홀드 타이머 정보 저장. 공급사가 holdcabin을 주지 않으면 60분 fallback 홀드를 사용한다.
                    try {
                        if (idx != null) {
                            const holdInfo = item0?.cruisedetail?.cabin?.holdcabin;
                            if (holdInfo?.holdtime && holdInfo?.releasetime) {
                                const holdDate = parseBasketHoldDate(holdInfo.holdtime);
                                const releaseDate = parseBasketHoldDate(holdInfo.releasetime);
                                if (holdDate && releaseDate) {
                                    const finalHold = isRestoring && preservedHold
                                        ? {
                                            iso: preservedHold.holdAt ?? holdDate.iso,
                                            localString: preservedHold.holdAtFormatted ?? holdDate.localString,
                                            raw: preservedHold.holdAtOriginal ?? holdInfo.holdtime,
                                        }
                                        : holdDate;
                                    const finalRelease = isRestoring && preservedHold
                                        ? {
                                            iso: preservedHold.releaseAt ?? releaseDate.iso,
                                            localString: preservedHold.releaseAtFormatted ?? releaseDate.localString,
                                            raw: preservedHold.releaseAtOriginal ?? holdInfo.releasetime,
                                        }
                                        : releaseDate;

                                    syncBasketHold({
                                        holdAt: finalHold.iso,
                                        releaseAt: finalRelease.iso,
                                        holdAtFormatted: finalHold.localString,
                                        releaseAtFormatted: finalRelease.localString,
                                        holdAtOriginal: finalHold.raw,
                                        releaseAtOriginal: finalRelease.raw,
                                        capturedAt: Date.now(),
                                    });
                                } else {
                                    syncBasketHold(buildFallbackBasketHold()); // 파싱 실패 시에도 60분 fallback 홀드로 유지
                                }
                            } else {
                                syncBasketHold(buildFallbackBasketHold()); // 공급사가 holdcabin을 주지 않아도 60분 fallback 홀드를 부여
                            }
                        }
                    } catch (_) { /* noop */ }

                } else {
                    alert({
                        title: '에러',
                        content: <p>예약정보를 가져오지 못했습니다. 잠시후 다시 시도해주세요.</p>,
                        showConfirmButton : false,
                        footerContent: <button type="button" className="btn">확인</button>,
                        action: resetBookingState,
                    });
                }

            } catch (err) {
                if (!isCurrentBasketRequest()) return;
                console.error("Cruise basket fetch error:", err);
                alert({
                    title: '에러',
                    content: <p>네트워크 오류가 발생했습니다. 네트워크 환경을 확인 후 다시 시도해 주세요.</p>,
                    showConfirmButton : false,
                    footerContent: <button type="button" className="btn">확인</button>,
                    action: resetBookingState,
                });
            } finally {
                if (basketFetchGuardRef.current.key === requestKey) {
                    basketFetchGuardRef.current.inFlight = false;
                    basketFetchGuardRef.current.lastAt = Date.now();
                }
                if (acquiredGlobalLock) {
                    releaseBasketFetchLock(requestKey);
                }
                // A stale response must not change the new screen or active booking.
                if (isCurrentBasketRequest()) setIsLoading(false);
            }
        };
        fetchCruiseBasket();
    }, [bookingPayload, resetBookingState, resolveScopedSessionKey, isRestoring, applyBasketData, bookingSubmissionBlocked, bookingSubmissionBlockKey, cruiseCodeToId, idx]);

    // --- Data for Price Display (from API) ---
    const basketResult = cruiseBasketInfo?.results?.[0];
    const basketItem = basketResult?.basketitems?.[0];
    const cruiseDetail = basketItem?.cruisedetail;
    const apiCabinName = cruiseDetail?.cabin?.name;
    const basketTotalPrice = basketResult?.totalprice || 0;
    const isPartialPaymentFlow = isPartialPaymentLine(lineId);
    const basketDepositAmount = isPartialPaymentFlow
        ? Number(basketResult?.totaldeposit ?? basketResult?.totalDeposit ?? basketResult?.total_deposit ?? 0)
        : 0;
    const promotionPreviewPayload = useMemo(() => {
        const totalAmount = Number(basketTotalPrice || 0);
        const grossprice = parsePromotionAmount(
            cruiseDetail?.grossprice
            ?? cruiseDetail?.grossPrice
            ?? basketResult?.grossprice
            ?? basketItem?.grossprice
        );
        if (!lineId || !totalAmount || !grossprice) {
            return null;
        }
        return {
            line_id: lineId,
            codetocruiseid: cruiseCodeToId,
            nights: cruiseDetail?.sailnights ?? cruiseDetail?.nights ?? bookingPayload?.detailData?.schedule?.nights,
            grossprice,
            total_amount: totalAmount,
            deposit_amount: basketDepositAmount,
        };
    }, [basketDepositAmount, basketItem?.grossprice, basketResult?.grossprice, basketTotalPrice, bookingPayload?.detailData?.schedule?.nights, cruiseCodeToId, cruiseDetail?.grossPrice, cruiseDetail?.grossprice, cruiseDetail?.nights, cruiseDetail?.sailnights, lineId]);
    const { summary: promotionPreviewSummary, refresh: refreshPromotionPreview, enabled: promotionPreviewEnabled } = usePromotionPreview(
        promotionPreviewPayload,
        () => submitInFlightRef.current || extraUpdateInFlightRef.current || bookingSubmissionBlocked
    );
    const totalPriceCalculated = parsePromotionAmount(promotionPreviewSummary?.total_amount) ?? basketTotalPrice;
    const depositAmountCalculated = parsePromotionAmount(promotionPreviewSummary?.deposit_amount) ?? basketDepositAmount;
    const finalAmountCalculated = parsePromotionAmount(promotionPreviewSummary?.final_amount)
        ?? Math.max(Number(totalPriceCalculated || 0) - depositAmountCalculated, 0);
    const finalDueDateCalculated = basketResult?.duedate || basketResult?.dueDate || basketResult?.due_date || null;
    const showPaymentBreakdown = isPartialPaymentFlow && depositAmountCalculated > 0;
    const diningOptions = cruiseDetail?.dining?.seatings || [];
    const translatedDiningOptions = useMemo(
        () => translateDiningOptions(diningOptions, dinnerTranslationRows),
        [diningOptions, dinnerTranslationRows]
    );

    const gratuityType = useMemo(() => {
        const normalizedLineId = Number(lineId);
        if (normalizedLineId === 16) return 'Hotel Service Charges';
        if ([17, 22, 73].includes(normalizedLineId)) return 'Gratuities';
        return '';
    }, [lineId]);

    const gratuityExtra = useMemo(() => {
        if (!gratuityType) return null;
        const extras = Array.isArray(basketItem?.optionalextras) ? basketItem.optionalextras : [];
        return findTraveltekGratuityExtra(extras, lineId);
    }, [basketItem?.optionalextras, gratuityType, lineId]);

    const selectedGratuity = useMemo(() => {
        if (!gratuityExtra) return null;
        return findSelectedTraveltekGratuity(gratuityExtra, basketItem?.selectedextras, lineId);
    }, [basketItem?.selectedextras, gratuityExtra, lineId]);

    const isGratuitySelected = useMemo(() => {
        return isTraveltekGratuitySelected(gratuityExtra, basketItem?.selectedextras, lineId);
    }, [basketItem?.selectedextras, gratuityExtra, lineId]);

    const handleGratuityToggle = async (event) => {
        const shouldAdd = event.target.checked;
        if (!gratuityExtra
            || bookingSubmissionBlocked
            || submitInFlightRef.current
            || extraUpdateInFlightRef.current
            || isLoading
            || isExtraUpdating) return;
        const sessionKey = resolveScopedSessionKey();
        const basketItemKey = basketItem?.itemkey;
        if (!sessionKey || !basketItemKey || gratuityExtra.resultno === undefined || gratuityExtra.resultno === null) return;
        extraUpdateInFlightRef.current = true;
        setIsExtraUpdating(true);
        try {
            const endpoint = shouldAdd ? '/traveltek/add_extra.php' : '/traveltek/remove_extra.php';
            const response = await apiGet(endpoint, {
                sessionkey: sessionKey,
                basketkey: basketItemKey,
                itemkey: basketItemKey,
                resultno: gratuityExtra.resultno,
                codetocruiseid: cruiseCodeToId,
                ship_id: bookingPayload?.detailData?.schedule?.ship_id,
            });
            applyBasketData(response);
        } catch (err) {
            console.error('extra update failed', err);
        } finally {
            extraUpdateInFlightRef.current = false;
            if (componentMountedRef.current) {
                setIsExtraUpdating(false);
            }
        }
    };

    const { passengers, warning: passengerMappingWarning } = useMemo(() => resolveTraveltekPassengers({
        counts: bookingPayload?.counts,
        criteria: cruiseBasketInfo?.meta?.criteria,
        cruiseDetail,
        childAges,
    }), [bookingPayload?.counts, cruiseBasketInfo?.meta?.criteria, cruiseDetail, childAges]);

    const passengerPriceDetails = useMemo(() => {
        if (!cruiseDetail?.perperson) return {};
        const detailsByGuest = {};

        const isOnboardCreditCategory = (category = {}) => {
            const normalized = `${category?.category || ''} ${category?.description || ''}`
                .toLowerCase()
                .replace(/[^a-z]/g, '');
            return normalized.includes('onboardcredit');
        };

        const isGratuityCategory = (category = {}) => {
            return isTraveltekGratuityPriceItem({
                category: category?.category || '',
                description: category?.description || '',
                lineId,
            });
        };

        cruiseDetail.perperson.forEach(category => {
            if (isOnboardCreditCategory(category)) return;

            const isGrats = isGratuityCategory(category);
            const prices = Array.isArray(category.prices) ? category.prices : [];

            // 선결제 미선택 시 선상팁은 아예 제외
            if (!isGratuitySelected && isGrats) return;

            const categoryLabel = category?.category || category?.description || '';
            prices.forEach(priceInfo => {
                if (priceInfo.price !== undefined) {
                    const { guestno, price } = priceInfo;
                    if (!detailsByGuest[guestno]) {
                        detailsByGuest[guestno] = [];
                    }
                    const normalizedPriceItem = normalizeTraveltekPassengerPriceItem({
                        category: categoryLabel,
                        description: category?.description || categoryLabel || '',
                        lineId,
                    });
                    const targetDesc = normalizedPriceItem.description;

                    // 동일 description 있으면 합산
                    const existing = detailsByGuest[guestno].find(item => (item?.description || '').toLowerCase() === targetDesc.toLowerCase());
                    if (existing) {
                        existing.price = (Number(existing.price) || 0) + (Number(price) || 0);
                    } else {
                        detailsByGuest[guestno].push({
                            category: normalizedPriceItem.category,
                            description: targetDesc,
                            price: price
                        });
                    }
                }
            });
        });
        const hasGratuityInPerperson = cruiseDetail.perperson.some(category => {
            return isTraveltekGratuityPriceItem({
                category: category?.category || '',
                description: category?.description || '',
                lineId,
            });
        });
        if (!hasGratuityInPerperson && isGratuitySelected) {
            const gratuityLabel = selectedGratuity?.description || gratuityExtra?.description || gratuityType || 'Gratuities';
            const gratuityCategory = gratuityType || gratuityLabel;
            const gratuityPriceSource = selectedGratuity?.price ?? gratuityExtra?.price;
            const gratuityQty = selectedGratuity?.quantity ?? gratuityExtra?.quantity ?? 1;
            const gratuityTotal = Number(gratuityPriceSource) * (Number(gratuityQty) || 1);
            if (Number.isFinite(gratuityTotal) && gratuityTotal > 0 && passengers.length > 0) {
                const perGuest = Math.round((gratuityTotal / passengers.length) * 100) / 100;
                passengers.forEach(pax => {
                    const guestNo = pax.guestNo;
                    if (!detailsByGuest[guestNo]) detailsByGuest[guestNo] = [];
                    const hasSame = detailsByGuest[guestNo].some(item => (item?.description || '').toLowerCase() === gratuityLabel.toLowerCase());
                    if (!hasSame) {
                        detailsByGuest[guestNo].push({
                            category: gratuityCategory,
                            description: gratuityLabel,
                            price: perGuest,
                        });
                    }
                });
            }
        }
        return detailsByGuest;
    }, [cruiseDetail, gratuityExtra, gratuityType, isGratuitySelected, lineId, passengers, selectedGratuity]);

    const passengerBenefitDetails = useMemo(() => {
        if (!cruiseDetail?.perperson) return {};
        const benefitsByGuest = {};

        cruiseDetail.perperson.forEach(category => {
            const categoryKey = (category?.category || '').toLowerCase();
            if (categoryKey !== 'onboardcredit') return;

            const prices = Array.isArray(category?.prices) ? category.prices : [];
            const categoryLabel = category?.category || category?.description || '';
            const rawDesc = category?.description || categoryLabel || 'On-Board Credit';

            prices.forEach(priceInfo => {
                if (priceInfo?.price === undefined) return;
                const guestNo = priceInfo?.guestno;
                if (!guestNo) return;
                if (!benefitsByGuest[guestNo]) {
                    benefitsByGuest[guestNo] = [];
                }

                const priceVal = Number(priceInfo.price) || 0;
                const existing = benefitsByGuest[guestNo].find(item => (item?.description || '').toLowerCase() === rawDesc.toLowerCase());
                if (existing) {
                    existing.price = (Number(existing.price) || 0) + priceVal;
                } else {
                    benefitsByGuest[guestNo].push({
                        category: categoryLabel,
                        description: rawDesc,
                        price: priceVal,
                    });
                }
            });
        });

        return benefitsByGuest;
    }, [cruiseDetail]);

    const passengerCountSummary = useMemo(() => {
        return passengers.reduce((acc, passenger) => {
            const key = passenger?.type;
            if (!key) return acc;
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
    }, [passengers]);

    const paxFareData = useMemo(() => {
        if (!Array.isArray(passengers) || passengers.length === 0) return [];

        const fareMap = {};
        Object.entries(passengerPriceDetails || {}).forEach(([guestNo, fareItems]) => {
            const paxIndex = Number(guestNo);
            if (!Number.isFinite(paxIndex)) return;

            const normalizedFareList = fareItems
                .filter(item => item && (item.description || item.price !== undefined))
                .map(item => ({
                    fare_name: item.description || '',
                    fare_amount: typeof item.price === 'number' ? item.price : Number(item.price) || 0,
                }));

            if (normalizedFareList.length > 0) {
                fareMap[paxIndex] = normalizedFareList;
            }
        });

        return passengers.map(pax => {
            const paxIdx = pax.guestNo;
            const fareList = fareMap[paxIdx] || [];
            return { pax_idx: paxIdx, fare_list: fareList };
        });
    }, [passengerPriceDetails, passengers]);

    // --- Data for Forms (from Payload) ---
    const initialUsersData = useMemo(() => {
        const result = [];
        const membershipPassengerInfo = normalizeMembershipPassengerInfo(bookingPayload?.membershipPassengerInfo);
        PASSENGER_TYPE_ORDER.forEach(type => {
            const count = Number(counts?.[type] || 0);
            for (let i = 0; i < count; i++) {
                const isFirstPassenger = result.length === 0;
                const membershipDefaults = isFirstPassenger && membershipPassengerInfo
                    ? {
                        lastName: membershipPassengerInfo.lastName || '',
                        firstName: membershipPassengerInfo.firstName || '',
                        gender: membershipPassengerInfo.gender || '',
                        birthDate: membershipPassengerInfo.birthDate || '',
                    }
                    : {};
                result.push({
                    key: `${type}-${i}`,
                    type,
                    lastName: '', firstName: '', gender: '', birthDate: '',
                    passportCountry: '', phoneCountryCode: '+82', phoneNumber: '', membershipNumber: '',
                    dinnerTime: '', bedType: '',
                    ...membershipDefaults,
                });
            }
        });
        return result;
    }, [counts, bookingPayload?.membershipPassengerInfo]);

    // --- State Management ---
    const [usersData, setUsersData] = useState(initialUsersData);
    const [errors, setErrors] = useState(() => Array.from({ length: initialUsersData.length }, () => ({})));
    const [ageTypeWarnings, setAgeTypeWarnings] = useState(() => Array(initialUsersData.length).fill(''));
    const [isFormValid, setIsFormValid] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 651);
    const [portalContainer, setPortalContainer] = useState(null);

    useEffect(() => {
        setUsersData(initialUsersData);
    }, [initialUsersData]);
    useEffect(() => {
        setAgeTypeWarnings(Array(initialUsersData.length).fill(''));
    }, [initialUsersData.length]);

    const validateAll = useCallback(() => {
        const newErrors = usersData.map((user, index) => {
            const userErrors = {};
            Object.keys(user).forEach(fieldName => {
                if (fieldName === 'dinnerTime' && index !== 0) {
                    return; // Skip dinnerTime validation for other users
                }
                const error = validateField(fieldName, user[fieldName]);
                if (error) {
                    userErrors[fieldName] = error;
                }
            });
            return userErrors;
        });
        setErrors(newErrors);
        return newErrors.every(userErrors => Object.keys(userErrors).length === 0);
    }, [usersData]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 651);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const footerEl = document.querySelector('.footer');
        if (!footerEl) return;
        const container = document.createElement('div');
        footerEl.parentNode.insertBefore(container, footerEl.nextSibling);
        setPortalContainer(container);
        return () => {
            if (footerEl.parentNode && container.parentNode === footerEl.parentNode) {
                footerEl.parentNode.removeChild(container);
            }
        };
    }, []);

    useLayoutEffect(() => {
        if (!portalContainer) return;
        const wrapper = portalContainer.firstElementChild;
        if (isMobile && wrapper) {
            const h = wrapper.getBoundingClientRect().height;
            portalContainer.style.paddingTop = `${h}px`;
        } else {
            portalContainer.style.paddingTop = '0px';
        }
    }, [portalContainer, isMobile]);

    useEffect(() => {
        const allFieldsFilled = usersData.every((user, index) => {
            const commonFields = user.lastName && user.firstName && user.gender && user.birthDate && user.passportCountry && user.phoneNumber;
            if (index === 0) {
                return commonFields && user.dinnerTime;
            }
            return commonFields;
        });
        const noErrors = errors.every(userErrors =>
            Object.values(userErrors).every(errorMessage => !errorMessage)
        );
        setIsFormValid(allFieldsFilled && noErrors);
    }, [usersData, errors]);

    // 생년월일 출항일 기준 - 에러문구
    const getAgeTypeWarning = useCallback((passengerType, birthDateValue) => {
        if (!departureDate || !birthDateValue) return '';
        const birth = new Date(birthDateValue);
        if (Number.isNaN(birth.getTime())) return '';

        let age = departureDate.getFullYear() - birth.getFullYear();
        const monthDiff = departureDate.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && departureDate.getDate() < birth.getDate())) {
            age -= 1;
        }
        if (!Number.isFinite(age)) return '';

        if (passengerType === 'adults') {
            if (age < adultMinAge) {
                return `출항일 기준 성인 나이(만 ${adultMinAge}세 이상)에 해당하지 않습니다.`;
            }
            return '';
        }

        if (passengerType === 'children') {
            if (childMinAge !== null && age < childMinAge) {
                return `출항일 기준 소아 최솟값(만 ${childMinAge}세 이상)을 만족하지 않습니다.`;
            }
            if (childMaxAge !== null && age > childMaxAge) {
                if (childMinAge !== null) {
                    return `출항일 기준 소아 나이 범위(만 ${childMinAge}~${childMaxAge}세)를 벗어났습니다.`;
                }
                return `출항일 기준 소아 최대 나이(만 ${childMaxAge}세 이하)를 초과했습니다.`;
            }
            if (age >= adultMinAge) {
                return `출항일 기준 성인 나이(만 ${adultMinAge}세 이상)로 분류됩니다.`;
            }
        }

        return '';
    }, [adultMinAge, childMinAge, childMaxAge, departureDate]);

    const handleUserInfoChange = (index, name, value) => {
        const newUsersData = [...usersData];
        newUsersData[index] = { ...newUsersData[index], [name]: value };
        setUsersData(newUsersData);

        const newErrors = [...errors];
        if (newErrors[index] && newErrors[index].hasOwnProperty(name)) {
            const updatedUserErrors = { ...newErrors[index] };
            updatedUserErrors[name] = '';
            newErrors[index] = updatedUserErrors;
            setErrors(newErrors);
        }
        if (name === 'birthDate') {
            setAgeTypeWarnings(prev => {
                const next = Array.isArray(prev) ? [...prev] : [];
                while (next.length < newUsersData.length) next.push('');
                next[index] = '';
                return next;
            });
        }
    };

    const handleBlur = (index, name, value) => {
        const error = validateField(name, value);
        const newErrors = [...errors];
        newErrors[index] = { ...newErrors[index], [name]: error };
        setErrors(newErrors);

        const newUsersData = [...usersData];
        const updatedUser = { ...newUsersData[index], [name]: value };
        newUsersData[index] = updatedUser;
        setUsersData(newUsersData);

        if (name === 'birthDate') {
            const warningMessage = getAgeTypeWarning(updatedUser.type, updatedUser.birthDate);
            setAgeTypeWarnings(prev => {
                const next = Array.isArray(prev) ? [...prev] : [];
                while (next.length < newUsersData.length) next.push('');
                next[index] = warningMessage;
                return next;
            });
        }
    };

    const handleSubmit = async (targetReservationType) => {
        if (submitInFlightRef.current
            || extraUpdateInFlightRef.current
            || bookingSubmissionBlocked
            || isPriceBlocked
            || isLoading
            || isExtraUpdating) {
            return;
        }

        const reservationType = targetReservationType === true;

        if (!validateAll()) {
            return;
        }

        const passengers = {};
        const localPassengers = {};
        const basketItemKey = cruiseBasketInfo?.results?.[0]?.basketitems?.[0]?.itemkey;
        const apiDining = cruiseDetail?.dining;
        const firstUser = usersData[0];
        const selectedDinnerCode = firstUser?.dinnerTime || '';
        const dinnerOption = Array.isArray(translatedDiningOptions)
            ? translatedDiningOptions.find(option => String(option?.code ?? '') === String(selectedDinnerCode))
            : null;
        const dinnerTimeDesc = dinnerOption?.displayDescription || dinnerOption?.description || selectedDinnerCode;
        const confirmPayload = dinnerTimeDesc
            ? { dinner_time: dinnerTimeDesc, dinner_time_code: selectedDinnerCode || null }
            : null;

        // allocation 객체 생성
        const allocation = { [basketItemKey]: {} };

        // dining 정보 구성
        const dining = {};
        dining.seating = firstUser?.dinnerTime || '';

        // tableSize정보 없을시 formData에 X  && 있으면 첫번째 값
        let tableSizeValue = firstUser.tablesize;
        if ((tableSizeValue === undefined || tableSizeValue === null) && apiDining?.tablesizes?.length > 0) {
            tableSizeValue = apiDining.tablesizes[0];
        }
        if (tableSizeValue !== undefined) {
            dining.tablesize = tableSizeValue.toString();
        }

        // 흡연여부 - smoking  값이 있으면 그대로 사용 및 없으면 'N'으로 고정
        if (apiDining?.smoking) {
            dining.smoking = apiDining.smoking;
        } else {
            dining.smoking = 'N'; // 250905 
        }

        allocation[basketItemKey].dining = dining;
        const normalizedPastPax = String(bookingPayload?.pastPax || '').trim();
        const isRoyalCaribbeanLine = Number(lineId) === 22;
        const isMscLine = Number(lineId) === 16;
        const isDisneyLine = Number(lineId) === 73;
        const includesPassengerContact = isMscLine || isDisneyLine;
        const normalizedUserEmail = String(userEmail || '').trim();

        usersData.forEach((user, idx) => {
            const paxNo = idx + 1;

            // 나이 계산 (만 나이)
            let age = null;
            if (user.birthDate) {
                const birth = new Date(user.birthDate);
                const today = new Date();
                let computedAge = today.getFullYear() - birth.getFullYear();
                const m = today.getMonth() - birth.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
                    computedAge -= 1;
                }
                age = Number.isFinite(computedAge) ? computedAge : null;
            }

            const derivePaxType = () => {
                if (age === null) {
                    return user.type === 'adults' ? 'adult' : 'children';
                }
                return age >= adultMinAge ? 'adult' : 'children';
            };

            const paxType = derivePaxType();
            const ageString = age === null ? '' : String(age);
            const mobileWithCountry = formatInternationalPhone(user.phoneCountryCode, user.phoneNumber);
            const membershipPayload = paxNo === 1 && normalizedPastPax
                ? (isRoyalCaribbeanLine
                    ? { rewards: normalizedPastPax }
                    : { pastpassengerno: normalizedPastPax })
                : {};

            // passengers 생성
            passengers[paxNo] = {
                age: ageString,
                dob: user.birthDate,
                ...membershipPayload,
                title: user.gender === 'male' ? 'Mr' : 'Ms',
                firstname: user.firstName,
                lastname: user.lastName,
                nationality: user.passportCountry,
                paxno: paxNo.toString(),
                paxtype: paxType,
                address: "55, Digital-ro 34-gil, Kolon Science Valley II, B201-F12",
                postcode: "08377",
                city: "Guro-gu",
                ...(includesPassengerContact && mobileWithCountry ? { telephone: mobileWithCountry } : {}),
                ...(includesPassengerContact && paxNo === 1 && normalizedUserEmail
                    ? { email: normalizedUserEmail }
                    : {}),
            };
            localPassengers[paxNo] = {
                ...passengers[paxNo],
                telephone: mobileWithCountry,
                phone: mobileWithCountry,
                mobile: mobileWithCountry,
            };
        });

        const primaryPassenger = firstUser; // 1번 승객 지정
        const primaryPhone = primaryPassenger //contact-phonenum 추출
            ? formatInternationalPhone(primaryPassenger.phoneCountryCode, primaryPassenger.phoneNumber)
            : '';
        // 성별 + Title
        const [title, genderCode] = (() => {
            if (!primaryPassenger?.gender) return ['', ''];
            if (primaryPassenger.gender === 'male') return ['Mr', 'M'];
            if (primaryPassenger.gender === 'female') return ['Ms', 'F'];
            return ['', ''];
        })();

        const contact = {
            // 와우크루즈 주소로 변경 - 250917
            country: "KR",
            county: "Seoul",
            city: "Guro-gu",
            postcode: "08377",
            address1: "55, Digital-ro 34-gil, Kolon Science Valley II, B201-F12",
            // 대표(1번승객 입력값)승객 입력값으로 - 250917
            title: title,
            gender: genderCode,
            telephone: primaryPhone,
            firstname: primaryPassenger?.firstName || "",
            middlename: primaryPassenger?.middleName || "",
            lastname: primaryPassenger?.lastName || "",
            // 로그인 유저 정보 - 250918
            email: userEmail || ""
        };

        const depositBookingFlag = isPartialPaymentLine(lineId) ? 1 : 0;
        const basketDepositMeta = depositBookingFlag === 1 ? {
            duedate: basketResult?.duedate || basketResult?.dueDate || basketResult?.due_date || null,
            totaldeposit: basketResult?.totaldeposit ?? basketResult?.totalDeposit ?? basketResult?.total_deposit ?? null,
        } : null;
        const formData = {
            allocation,
            passengers,
            depositbooking: depositBookingFlag,
            importtarget: "iBos",
            sessionkey: resolveScopedSessionKey(),
            contact,
        };
        if (Number.isFinite(Number(lineId)) && Number(lineId) > 0) {
            formData.line_id = Number(lineId);
        }
        const submissionAttempt = {
            scheduleId: idx == null ? '' : String(idx),
            sessionKey: String(formData.sessionkey || ''),
        };

        const paxData = paxFareData.map(pax => ({
            pax_idx: pax.pax_idx,
            fare_list: pax.fare_list.map(fare => ({ ...fare })),
        }));

        const passengerPayloadBase = {
            formDataInfo: formData,
            passengers: localPassengers,
            basketData: cruiseBasketInfo,
            paxFareInfo: paxData,
            ...(basketDepositMeta ? { depositMeta: basketDepositMeta } : {}),
            ...(confirmPayload ? { confirm: confirmPayload } : {}),
        };

        // ★★★ 로딩 시작 ★★★
        submitInFlightRef.current = true;
        setIsLoading(true);
        setIsBookingRequestPending(true);
        activeSubmissionAttemptRef.current = submissionAttempt;
        const handleBookingResponse = (response, { httpStatus = null, transportError = false } = {}) => {
            const currentScopedSessionKey = resolveScopedSessionKey();
            const latestContext = latestBookingContextRef.current;
            const isCurrentAttempt = componentMountedRef.current
                && activeSubmissionAttemptRef.current === submissionAttempt
                && latestContext.scheduleId === submissionAttempt.scheduleId
                && latestContext.sessionKey === submissionAttempt.sessionKey
                && currentScopedSessionKey === submissionAttempt.sessionKey;
            if (!isCurrentAttempt) {
                // 요청 도중 뒤로가기/선실 재선택/세션 교체가 발생했다면 이전 응답을
                // 최신 화면 상태에 합치지 않는다. 요청 전 저장한 차단은 그대로 두어
                // 실제 예약 결과를 마이페이지/고객센터에서 확인하게 한다.
                console.warn('Ignored stale Traveltek booking response after booking context changed.');
                return;
            }

            const decision = classifyBookingSubmissionResponse({ response, httpStatus, transportError, expectedSessionKey: submissionAttempt.sessionKey });
            const {
                traveltekResponse,
                hasFailedStatus,
                successFlag,
                canProceedWithBooking,
            } = decision.signals;
            const traveltekErrors = Array.isArray(traveltekResponse?.errors) ? traveltekResponse.errors : [];
            const topLevelErrors = Array.isArray(response?.errors) ? response.errors : [];
            const combinedErrors = [...traveltekErrors, ...topLevelErrors].filter(Boolean);

            if (decision.policy === BOOKING_SUBMISSION_POLICY.SUCCESS && canProceedWithBooking) {
                // 동일 탭에서 이전 시도의 차단키가 남아 있더라도 성공 응답이 확정되면 제거한다.
                clearBookingSubmissionBlock();
                const passengerPayload = {
                    ...passengerPayloadBase,
                    bookingResult: response,
                    reservation_create_date: response?.reservation_create_date || null,
                };

                const stepIncrement = reservationType === false ? 2 : 1;
                onNextStep({ reservationType, passengerInfo: passengerPayload }, stepIncrement);
                return;
            }

            const extractErrorMessage = (errorItem) => {
                if (!errorItem) return null;
                if (typeof errorItem === 'string') return errorItem;
                if (typeof errorItem === 'object') {
                    return errorItem.message || errorItem.text || errorItem.msg || errorItem.description || null;
                }
                return String(errorItem);
            };

            const messageParts = [];

            if (hasFailedStatus) {
                messageParts.push('선택하신 객실 예약이 완료되지 않았습니다.');
            }

            const explicitMsg = [response?.msg, response?.message]
                .find(value => typeof value === 'string' && value.trim())
                ?.trim() || '';
            if (explicitMsg) {
                messageParts.push(explicitMsg);
            }

            if (!successFlag && !explicitMsg) {
                messageParts.push('예약 요청에 실패했습니다.');
            }

            const errorText = extractErrorMessage(combinedErrors[0]);
            if (errorText) {
                messageParts.push(errorText);
            }

            const uniqueMessageParts = [...new Set(messageParts.filter(Boolean))];
            if (uniqueMessageParts.length === 0) {
                uniqueMessageParts.push('예약 요청에 실패했습니다. 잠시 후 다시 시도해주세요.');
            }

            let persistedBlock = null;
            if (decision.persistBlock) {
                persistedBlock = blockBookingSubmission(decision, response, formData.sessionkey);
            } else {
                clearBookingSubmissionBlock();
            }
            // 실패 세션을 특정하지 못하면 createBookingSubmissionBlock이 안전 재시작을
            // 수동 확인 상태로 낮춘다.
            const effectivePolicy = persistedBlock?.policy || decision.policy;
            const requestId = response?.request_id ?? response?.requestId ?? null;
            const visibleMessageParts = effectivePolicy === BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW
                ? BOOKING_MANUAL_REVIEW_MESSAGES
                : uniqueMessageParts;
            const errorContent = (
                <p>
                    {visibleMessageParts.map((part, messageIndex) => (
                        <React.Fragment key={`${messageIndex}-${part}`}>
                            {messageIndex > 0 && <br />}
                            {part}
                        </React.Fragment>
                    ))}
                    {effectivePolicy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE && (
                        <>
                            <br />
                            현재 화면의 예약 정보를 초기화한 뒤 처음부터 다시 진행해 주세요.
                        </>
                    )}
                    {effectivePolicy === BOOKING_SUBMISSION_POLICY.RETRYABLE_PRECHECK && (
                        <>
                            <br />
                            {decision.requiresInputCorrection
                                ? '입력한 탑승객 정보는 유지됩니다. 식사 시간 및 테이블 옵션을 확인한 뒤 다시 시도해 주세요.'
                                : '예약 요청 전 단계에서 처리되지 않았습니다. 입력 및 로그인 상태를 확인한 뒤 다시 시도해 주세요.'}
                        </>
                    )}
                    {requestId && (
                        <>
                            <br />
                            문의번호: {requestId}
                        </>
                    )}
                </p>
            );

            console.error('Booking API Error:', uniqueMessageParts);
            if (effectivePolicy === BOOKING_SUBMISSION_POLICY.AUTH_REQUIRED) {
                const redirectUrl = `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`;
                alert({
                    title: '로그인이 필요합니다.',
                    content: errorContent,
                    confirmButtonText: '취소',
                    footerContent: <button type="button" className="btn">로그인</button>,
                    action: () => {
                        if (componentMountedRef.current) {
                            navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
                        }
                    },
                });
                return;
            }

            if (effectivePolicy === BOOKING_SUBMISSION_POLICY.RETRYABLE_PRECHECK) {
                if (decision.requiresSessionRefresh) {
                    alert({
                        title: '예약 세션을 새로 시작해 주세요.',
                        content: errorContent,
                        showConfirmButton: false,
                        footerContent: <button type="button" className="btn">예약 정보 초기화 후 처음부터 진행</button>,
                        action: resetBookingState,
                    });
                    return;
                }
                alert({
                    title: '예약 요청을 확인해 주세요.',
                    content: errorContent,
                });
                return;
            }

            if (effectivePolicy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE) {
                const checkedRecovery = isAutomaticBookingRecovery(response, submissionAttempt.sessionKey);
                const resetSource = checkedRecovery
                    ? (response.recovery_code === 'MISSING_BOOKING_IDENTIFIERS' ? 'missing_booking_identifiers' : 'senior_fare_rejected')
                    : 'confirmed_failure_action';
                // The default alert button only closes the modal. Use its action
                // button so cleanup/navigation happens exclusively on confirmation.
                alert({
                    title: '예약을 다시 진행해 주세요.',
                    content: response.recovery_code === 'MISSING_BOOKING_IDENTIFIERS'
                        ? <p>현재 예약 요청이 집중되어 처리 중 오류가 발생했습니다.<br />잠시 후 예약을 다시 진행해 주세요.</p>
                        : errorContent,
                    showConfirmButton: false,
                    footerContent: <button type="button" className="btn">확인</button>,
                    action: () => requestSafeBookingReset(persistedBlock, resetSource),
                });
                return;
            }

            alert({
                title: '예약 상태 확인이 필요합니다.',
                content: errorContent,
                confirmButtonText: '닫기',
                footerContent: <button type="button" className="btn">마이페이지에서 예약 확인</button>,
                action: () => {
                    if (componentMountedRef.current) navigate('/mypage');
                },
            });
        };

        try {
            // const response = await apiPost('/reservation/create_reservation.php', { traveltek: formData });
            let freshPromotion = null;
            if (promotionPreviewEnabled) {
                try {
                    freshPromotion = await refreshPromotionPreview();
                    const latestContext = latestBookingContextRef.current;
                    if (!componentMountedRef.current || activeSubmissionAttemptRef.current !== submissionAttempt ||
                        latestContext.scheduleId !== submissionAttempt.scheduleId ||
                        String(resolveScopedSessionKey() || '') !== submissionAttempt.sessionKey) return;
                    if (!freshPromotion?.promotion_signature) throw new Error('PROMOTION_API_NOT_READY');
                    if (promotionPreviewChanged(promotionPreviewSummary, freshPromotion.payment_summary)) {
                        alert({ title: '프로모션 요금이 갱신되었습니다.', content: <p>변경된 할인 및 총 요금을 확인한 뒤 다시 예약 버튼을 눌러주세요.</p> });
                        return;
                    }
                } catch (error) {
                    if (componentMountedRef.current) alert({ title: '요금 확인이 필요합니다.', content: <p>프로모션 요금을 확인하지 못했습니다. 잠시 후 다시 시도해주세요. 예약 요청은 전송되지 않았습니다.</p> });
                    return;
                }
            }
            const requestPayload = {
                traveltek: formData,
                pax_fare_info: paxData,
                defer_payment_flow: reservationType,
                reservation: {
                    codetocruiseid: cruiseCodeToId || null,
                    schedule_idx: idx != null ? idx : null,
                    line_code: lineId || null,
                    line_id: lineId || null,
                    ship_code: bookingPayload?.detailData?.schedule?.ship_id || null,
                    ship_id: bookingPayload?.detailData?.schedule?.ship_id || null,
                },
                passengers: localPassengers,
            };
            if (freshPromotion) requestPayload.promotion_expected = freshPromotion.promotion_signature;
            if (Number.isFinite(Number(lineId)) && Number(lineId) > 0) {
                requestPayload.line_id = Number(lineId);
            }
            const promotionGrossprice = parsePromotionAmount(
                cruiseDetail?.grossprice
                ?? cruiseDetail?.grossPrice
                ?? basketResult?.grossprice
                ?? basketItem?.grossprice
            );
            if (promotionGrossprice) {
                requestPayload.promotion_context = {
                    line_id: lineId,
                    codetocruiseid: cruiseCodeToId,
                    nights: cruiseDetail?.sailnights ?? cruiseDetail?.nights ?? bookingPayload?.detailData?.schedule?.nights,
                    grossprice: promotionGrossprice,
                };
            }
            if (basketDepositMeta) {
                requestPayload.deposit_meta = basketDepositMeta;
            }
            if (confirmPayload) {
                requestPayload.confirm = confirmPayload;
            }
            // Persist only immediately before the real booking request, never while
            // merely refreshing a promotion. A failed preview must not lock the session.
            const inFlightBlock = createBookingSubmissionBlock({
                policy: BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW,
                reason: 'booking_request_in_flight',
            }, {
                scheduleId: idx,
                expectedSessionKey: formData.sessionkey,
            });
            if (!inFlightBlock || !persistBookingSubmissionBlock(inFlightBlock)) {
                alert({
                    title: '예약 요청을 진행할 수 없습니다.',
                    content: <p>중복 예약 방지를 위한 브라우저 저장 상태를 확인하지 못했습니다.<br />페이지를 닫지 말고 고객센터에 문의해 주세요.</p>,
                });
                return;
            }
            const response = await apiPost('/reservation/create_reservation.php', requestPayload);
            handleBookingResponse(response, { httpStatus: 200 });
        } catch (error) {
            console.error('Error sending booking request:', error);

            // Axios는 4xx/5xx를 예외로 던지므로 응답 본문이 있으면 일반 예약 실패와
            // 동일하게 해석한다. 실제 HTTP 응답을 네트워크 장애로 오인하지 않는다.
            if (error?.response) {
                const responseData = error.response.data;
                const bookingErrorResponse = responseData && typeof responseData === 'object'
                    ? responseData
                    : {
                        success: false,
                        msg: typeof responseData === 'string' ? responseData.trim() : '',
                    };
                handleBookingResponse(bookingErrorResponse, {
                    httpStatus: error.response.status,
                });
                return;
            }

            handleBookingResponse({
                success: false,
                message: '예약 요청 중 네트워크 오류가 발생했습니다.',
            }, { transportError: true });
        } finally {
            // ★★★ 로딩 종료 (성공/실패 무관) ★★★
            if (activeSubmissionAttemptRef.current === submissionAttempt) {
                activeSubmissionAttemptRef.current = null;
                submitInFlightRef.current = false;
            }
            if (componentMountedRef.current) {
                setIsLoading(false);
                setIsBookingRequestPending(false);
            }
        }
    };

    const ageLabelMap = passengerLabels;

    const availableActions = useMemo(() => {
        const num = Number(lineId);
        if (Number.isFinite(num)) {
            if (num === 16 || PARTIAL_PAYMENT_LINE_IDS.includes(num)) return ['payment'];
        }
        return ['request', 'payment'];
    }, [lineId]);

    const showRequest = availableActions.includes('request');
    const showPayment = availableActions.includes('payment');

    if (!bookingPayload) {
        return <div>Loading...</div>; // or some other placeholder
    }

    const requestInfoBtnContent = (
        <div className='depth_3-suggest-btn-wrapper' style={{display:'flex', flexDirection:'column', gap:'15px'}}>
            {bookingSubmissionBlocked && !isBookingRequestPending && (
                <div style={{display:'flex', flexDirection:'column', gap:'10px', fontSize:'14px', lineHeight:'150%', color:'#d14343'}}>
                    <span>
                        {bookingSubmissionBlock?.policy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE
                            ? (bookingSubmissionBlock.reason === 'cabin_reselection_required'
                                ? '객실 정보를 확인하지 못했습니다. 객실을 다시 선택해 주세요.'
                                : '실패한 예약 정보는 다시 사용할 수 없습니다. 현재 화면의 예약 정보를 초기화한 뒤 처음부터 다시 진행해 주세요.')
                            : <>{BOOKING_MANUAL_REVIEW_MESSAGES[0]}<br />{BOOKING_MANUAL_REVIEW_MESSAGES[1]}</>}
                        {bookingSubmissionBlock?.requestId && (
                            <><br />문의번호: {bookingSubmissionBlock.requestId}</>
                        )}
                    </span>
                    {bookingSubmissionBlock?.policy === BOOKING_SUBMISSION_POLICY.RESTART_SAFE
                        && bookingSubmissionBlock?.expectedSessionKey
                        && typeof onResetBookingFlow === 'function' && (
                        <button
                            type="button"
                            className="btn"
                            onClick={() => requestSafeBookingReset(bookingSubmissionBlock, 'blocked_banner_action')}
                        >
                            {bookingSubmissionBlock.reason === 'cabin_reselection_required'
                                ? '객실 다시 선택' : '예약 정보 초기화 후 처음부터 진행'}
                        </button>
                    )}
                    {bookingSubmissionBlock?.policy === BOOKING_SUBMISSION_POLICY.MANUAL_REVIEW && (
                        <button
                            type="button"
                            className="btn"
                            onClick={() => {
                                if (componentMountedRef.current) navigate('/mypage');
                            }}
                        >
                            마이페이지에서 예약 확인
                        </button>
                    )}
                </div>
            )}
            {showRequest && (
                <RequestInfoBtn
                    reservationType={false}
                    onClick={() => handleSubmit(false)}
                    disabled={isPriceBlocked || !isFormValid || isLoading || isExtraUpdating || bookingSubmissionBlocked}
                />
            )}
            {showPayment && (
                <RequestInfoBtn
                    reservationType={true}
                    onClick={() => handleSubmit(true)}
                    disabled={isPriceBlocked || !isFormValid || isLoading || isExtraUpdating || bookingSubmissionBlocked}
                />
            )}
        </div>
    );

    return (
        <>
            {/* ★★★ 3. 로딩 상태일 때 화면 전체를 덮는 오버레이 표시 ★★★ */}
            {(isLoading || isExtraUpdating) && (
                <Loading isLoading={true} />
            )}
            <div className="guest-write-info">
                <div className="guest-info-section">
                    <div className="before-write-info-caution">
                        <span>*여권 정보와 동일하게 기재해 주시기 바랍니다.</span>
                        <span>*오기재로 인한 철자 변경 시 수수료가 부과됩니다.</span>
                    </div>
                    {gratuityExtra && (
                        <div style={{ margin: '12px 0 20px 0' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', color: '#191F28' }}>
                                <input
                                    type="checkbox"
                                    checked={isGratuitySelected}
                                    onChange={handleGratuityToggle}
                                    disabled={isExtraUpdating || isLoading || isPriceBlocked || bookingSubmissionBlocked}
                                />
                                선상팁 선결제
                            </label>
                        </div>
                    )}
                    <div className='user-form-wrapper'>
                        {PASSENGER_TYPE_ORDER.flatMap(type => {
                            const count = Number(counts?.[type] || 0);
                            return Array.from({ length: count }).map((_, i) => {
                                const userIndex = usersData.findIndex(u => u.key === `${type}-${i}`);
                                if (userIndex === -1) return null;
                                const user = usersData[userIndex];
                                return (
                                    <UserInfo
                                        key={user.key}
                                        type={user.type}
                                        idx={i + 1}
                                        idSuffix={user.key || userIndex + 1}
                                        displayIdx={i + 1}
                                        userData={user}
                                        onDataChange={(...args) => handleUserInfoChange(userIndex, ...args)}
                                        onBlur={(...args) => handleBlur(userIndex, ...args)}
                                        errors={errors[userIndex]}
                                        diningOptions={translatedDiningOptions}
                                        formDisabled={isPriceBlocked}
                                        isRepresentative={userIndex === 0}
                                        passengerLabels={passengerLabels}
                                        ageTypeWarning={ageTypeWarnings[userIndex]}
                                    />
                                );
                            });
                        })}
                    </div>
                </div>

                <div className='guest-price-info-wrapper'>
                    <div className='guest-price-info'>
                        <div className="price-header">
                            <h2>{apiCabinName || payloadCabinName}</h2>
                            {bookingPayload?.userSelectCabinInfo?.deckname && (
                                <div>
                                    층수 {bookingPayload.userSelectCabinInfo.deckname.split(' ')[1]}F <span className='divider' /> 선실번호 {bookingPayload.userSelectCabinInfo.cabinno} <span className='divider' /> {bookingPayload.userSelectCabinInfo.location}
                                </div>
                            )}
                            <div>
                                {PASSENGER_TYPE_ORDER
                                    .map(type => ({ type, count: passengerCountSummary[type] || 0 }))
                                    .filter(item => item.count > 0)
                                    .map((item, index, arr) => (
                                        <React.Fragment key={item.type}>
                                            <span>{ageLabelMap[item.type]} {item.count}</span>
                                            {index < arr.length - 1 && <span className="divider" />}
                                        </React.Fragment>
                                    ))}
                            </div>
                        </div>

                        <div className="price-body">
                            {passengerMappingWarning && <p role="alert">{passengerMappingWarning}</p>}
                            {isPriceBlocked ? (
                                <div style={{color:'#191f28',fontSize:'16px', fontWeight:'400', lineHeight:'160%'}}>해당 객실의 요금을 확인할 수 없어 예약을 진행할 수 없습니다.</div>
                            ) : (!cruiseDetail ? <div>가격 정보를 불러오는 중입니다...</div> : (
                                passengers.map(pax => {
                                    const guestNo = pax.guestNo;
                                    const priceDetails = passengerPriceDetails[guestNo] || [];
                                    const benefitDetails = passengerBenefitDetails[guestNo] || [];
                                    const paxLabel = ageLabelMap[pax?.type] || '탑승객';
                                    const totalForPax = priceDetails.reduce((sum, item) => sum + (Number(item?.price) || 0), 0);
                                    return (
                                        <Accordion key={`guest-${guestNo}`} flush>
                                            <Accordion.Item eventKey={`guest-${guestNo}`}>
                                                <Accordion.Header>
                                                    <div>
                                                        {paxLabel} {pax?.typeIndex || guestNo} <ArrowCircleIcon />
                                                    </div>
                                                    <p>{Number(totalForPax).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    {priceDetails.map((item, idx) => {
                                                        const desc = getTraveltekPriceLabel(item, priceTranslations);
                                                        const amt = Number(item?.price) || 0;
                                                        return (
                                                            <div key={idx} className='detail-price-info'>
                                                                <span>{desc}</span>
                                                                <span>{amt.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                                                            </div>
                                                        );
                                                    })}
                                                    {benefitDetails.length > 0 && (
                                                        <div className='benefit-price-group'>
                                                            <div className='benefit-price-head'>
                                                                <span className='benefit-price-title'>특별 혜택</span>
                                                                <span className='benefit-price-note'>선내에서 사용 가능한 무료 리워드 크레딧</span>
                                                            </div>
                                                            {benefitDetails.map((item, idx) => {
                                                                const categoryKey = (item?.category || '').toString().toLowerCase();
                                                                const desc = getTraveltekPriceLabel(item, priceTranslations,
                                                                    categoryKey === 'onboardcredit' ? 'ON-BOARD CREDIT' : undefined);
                                                                const amt = Number(item?.price) || 0;
                                                                return (
                                                                    <div key={`benefit-${idx}`} className='detail-price-info benefit-price-info'>
                                                                        <span>{desc}</span>
                                                                        <span>{amt.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {priceDetails.length === 0 && benefitDetails.length === 0 && (
                                                        <div className='detail-price-info'>
                                                            <span>가격 정보 없음</span>
                                                            <span>-</span>
                                                        </div>
                                                    )}
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    );
                                })
                            ))}
                        </div>

                        <div className='price-footer'>
                            <PromotionDiscountRows
                                paymentSummary={promotionPreviewSummary}
                                formatCurrency={formatUsdCurrency}
                            />
                            <div className='total-price-wrapper'>
                                <span>총 요금</span>
                                <span className='total-price'>{formatUsdCurrency(totalPriceCalculated)}</span>
                            </div>
                            {showPaymentBreakdown && (
                                <div className='payment-breakdown'>
                                    <div className='payment-line'>
                                        <span className='label'>예약금</span>
                                        <span className='amount'>{formatUsdCurrency(depositAmountCalculated)}</span>
                                        <span className='status'>즉시 결제</span>
                                    </div>
                                    <div className='payment-line'>
                                        <span className='label'>잔금</span>
                                        <span className='amount'>{formatUsdCurrency(finalAmountCalculated)}</span>
                                        <span className='status'>{formatDueDateStatus(finalDueDateCalculated)}</span>
                                    </div>
                                </div>
                            )}
                            {!isMobile && requestInfoBtnContent}
                        </div>
                    </div>
                </div>
            </div>
            {isMobile && portalContainer && ReactDOM.createPortal(requestInfoBtnContent, portalContainer)}
        </>
    );
}
export default GuestWriteInfo; 
